/**
 * WebAuthn utility functions for passkey authentication
 */

/**
 * Convert base64url to ArrayBuffer
 */
export function base64UrlToArrayBuffer(base64Url) {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '==='.slice(0, (4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return buffer;
}

/**
 * Convert ArrayBuffer to base64url
 */
export function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  const base64 = btoa(binary);

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Prepare WebAuthn credential options received from server
 */
export function preparePublicKeyOptions(options) {
  // Convert challenge and user ID to ArrayBuffer
  const publicKey = {
    ...options,
    challenge: base64UrlToArrayBuffer(options.challenge),
  };

  // Convert user.id to ArrayBuffer if this is a registration request
  if (publicKey.user && publicKey.user.id) {
    publicKey.user = {
      ...publicKey.user,
      id: base64UrlToArrayBuffer(publicKey.user.id),
    };
  }

  // Convert allowCredentials id array to ArrayBuffer (for authentication)
  if (publicKey.allowCredentials) {
    publicKey.allowCredentials = publicKey.allowCredentials.map(credential => ({
      ...credential,
      id: base64UrlToArrayBuffer(credential.id),
    }));
  }

  // Convert excludeCredentials id array to ArrayBuffer (for registration)
  if (publicKey.excludeCredentials) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map(credential => ({
      ...credential,
      id: base64UrlToArrayBuffer(credential.id),
    }));
  }

  return publicKey;
}

/**
 * Prepare WebAuthn credential response for server
 */
export function preparePublicKeyCredential(credential) {
  const { id, rawId, response, type } = credential;

  // Convert all ArrayBuffers to base64url
  const result = {
    id,
    rawId: arrayBufferToBase64Url(rawId),
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
    },
    type,
  };

  // Add authenticatorData and signature if this is an authentication response
  if (response.authenticatorData) {
    result.response.authenticatorData = arrayBufferToBase64Url(response.authenticatorData);
    result.response.signature = arrayBufferToBase64Url(response.signature);
    if (response.userHandle) {
      result.response.userHandle = arrayBufferToBase64Url(response.userHandle);
    }
  }

  // Add attestationObject if this is a registration response
  if (response.attestationObject) {
    result.response.attestationObject = arrayBufferToBase64Url(response.attestationObject);
  }

  // Add device information
  result.deviceInfo = {
    platform: navigator.platform,
    userAgent: navigator.userAgent,
  };

  return result;
}

/**
 * Check if we're running inside a React Native WebView
 */
export function isReactNativeWebView() {
  return !!window.ReactNativeWebView;
}

/**
 * Check if WebAuthn/passkey is supported in this browser
 * Returns true for React Native WebView since native passkey is available
 */
export function isWebAuthnSupported() {
  return window.PublicKeyCredential !== undefined || isReactNativeWebView();
}

/**
 * Check if platform authenticator (biometrics) is available
 * For React Native WebView, returns true since native passkey handles this
 */
export async function isPlatformAuthenticatorAvailable() {
  if (isReactNativeWebView()) {
    return true;
  }
  if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
    return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  }
  return false;
}

/**
 * Normalize a passkey assertion from React Native (base64url strings) for API validation.
 * react-native-passkey may omit `type` or send userHandle: null; OmmnioSign verify rejects those.
 */
export function prepareProxyAssertion(credential) {
  if (!credential?.id || !credential?.response) {
    throw new Error('Invalid passkey assertion');
  }

  const { response } = credential;

  if (!response.authenticatorData || !response.clientDataJSON || !response.signature) {
    throw new Error('Invalid passkey assertion');
  }

  const assertion = {
    id: credential.id,
    rawId: credential.rawId || credential.id,
    type: credential.type || 'public-key',
    response: {
      authenticatorData: response.authenticatorData,
      clientDataJSON: response.clientDataJSON,
      signature: response.signature,
    },
  };

  if (response.userHandle) {
    assertion.response.userHandle = response.userHandle;
  }

  return assertion;
}

/**
 * Proxy WebAuthn create (registration) to React Native
 * Returns a promise that resolves with the credential
 */
export function proxyWebAuthnCreate(options) {
  return new Promise((resolve, reject) => {
    const requestId = `webauthn-create-${Date.now()}`;

    const handleResponse = event => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'OMMNIOSIGN_WEBAUTHN_CREATE_RESPONSE' && data?.requestId === requestId) {
          window.removeEventListener('message', handleResponse);
          if (data.success) {
            resolve(data.credential);
          } else {
            reject(new Error(data.error || 'Passkey registration failed'));
          }
        }
      } catch {
        // Ignore parse errors
      }
    };

    window.addEventListener('message', handleResponse);

    // Send request to React Native - keep base64url format (don't convert to ArrayBuffer)
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'OMMNIOSIGN_WEBAUTHN_CREATE_REQUEST',
        requestId,
        publicKeyOptions: options, // Already in base64url format from server
      }),
    );

    // Timeout after 5 minutes
    setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      reject(new Error('Passkey registration timeout'));
    }, 300000);
  });
}

/**
 * Check if conditional UI (autofill) is supported
 */
export function isConditionalMediationSupported() {
  return (
    window.PublicKeyCredential &&
    window.PublicKeyCredential.isConditionalMediationAvailable &&
    typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function'
  );
}

/**
 * Android SMS Retriever app hash injected by the RN DigitalSignatureScreen WebView.
 */
export function getAndroidAppHashFromBridge() {
  const hash = window.__OMMNIO_ANDROID_APP_HASH__;
  return typeof hash === 'string' && hash.length === 11 ? hash : null;
}

/**
 * Error handling utilities
 */
export function getWebAuthnError(error) {
  // Check for known WebAuthn error codes
  if (error.name === 'NotAllowedError') {
    return {
      code: 'user_cancelled',
      message: 'Authentication was cancelled by the user',
    };
  }
  if (error.name === 'SecurityError') {
    return {
      code: 'security_error',
      message: 'The operation is not allowed by the user agent or the platform',
    };
  }
  if (error.name === 'AbortError') {
    return {
      code: 'aborted',
      message: 'The operation was aborted',
    };
  }
  if (error.name === 'NotSupportedError') {
    return {
      code: 'not_supported',
      message: 'The authenticator does not support the requested operation',
    };
  }

  // Default error
  return {
    code: 'unknown_error',
    message: error.message || 'An unknown error occurred',
  };
}
